"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const lodash_1 = require("lodash");
const useHashAsState_1 = __importDefault(require("../../hooks/useHashAsState"));
/**
 * Tabs
 * @block
 * @requiresHooks
 */
const Tabs = ({ children, defaultActiveTab = 0, className, classNames = [], trackStateInHash = false, }) => {
    const ref = (0, react_1.useRef)(null);
    // We'll use a Memo to calculate our tabs.  We're basing it off of the children passed in rather than DOM elements.
    const tabs = (0, react_1.useMemo)(() => {
        const tabs = [];
        (Array.isArray(children) ? children : (children ? [children] : [])).forEach((child) => {
            // We're just going to have to treat each child like a "Tab" element.  There's no way in a production
            // build to check what kind of component the child is.
            tabs.push([child.props.id || (0, lodash_1.kebabCase)(child.props.title), child.props.title]);
        });
        return tabs;
    }, [children]);
    // Helper function to tag a tag slug like my-first-tab and figure out the index amongst our tabs
    const convertTabNameToTabIndex = (tabName) => {
        let tabIndex = null;
        tabs.forEach((tab, i) => {
            if (tab[0] == tabName) {
                tabIndex = i;
            }
        });
        return tabIndex || 0;
    };
    // If we're not tracking state in hash, then we pass in an empty config to useStateAsHash
    const [state, , updateState] = (0, useHashAsState_1.default)(trackStateInHash ? {
        parameters: {
            tab: 'string',
        },
    } : {});
    // The initial state has to calculate every time, but it's only actually used on the first pass through the
    // subsequent useState() call.
    let initialTab = state.tab ? convertTabNameToTabIndex(state.tab) : defaultActiveTab;
    const [activeTab, setActiveTab] = (0, react_1.useState)(initialTab);
    // I'm using the tab slug as the dependency here as it's nicer than having both activeTab and tabs as dependencies
    // The following useEffect is in place to update the hash state whenever the tab changes.
    const expectedTab = tabs[activeTab] ? tabs[activeTab][0] : null;
    (0, react_1.useEffect)(() => {
        if (trackStateInHash) {
            updateState({ tab: expectedTab });
        }
    }, [expectedTab, trackStateInHash]);
    // We'll use the layout effect to add and remove the active class on the tab panes.  Because they're rendered as
    // children, we don't have access to the props to update the classNames declaratively, so we have to do DOM
    // manipulation.
    (0, react_1.useLayoutEffect)(() => {
        if (!ref.current) {
            return;
        }
        ref.current.querySelectorAll('.tab-pane').forEach((tab, i) => {
            if (i == activeTab) {
                tab.classList.add('active');
            }
            else {
                tab.classList.remove('active');
            }
        });
    }, [activeTab]);
    /**
     * Bootstrap is not actually taking care of switching tabs.  We are doing that ourselves by
     * switching the "activeTab".  We still want to trigger the BS tab events though as other
     * parts of the system might rely on those firing properly.  This isn't a 100% solution however
     * because I'm not able to pass in the "relatedTarget", like is normally on the BS event.
     *
     * @param tabID
     */
    const setActiveTabWithEvents = (tabID) => {
        if (activeTab) {
            const previous = document.querySelector('[data-bs-target="#' + tabs[activeTab][0] + '"]');
            previous === null || previous === void 0 ? void 0 : previous.dispatchEvent(new Event('hide.bs.tab'));
            setTimeout(() => {
                previous === null || previous === void 0 ? void 0 : previous.dispatchEvent(new Event('hidden.bs.tab'));
            });
        }
        const next = document.querySelector('[data-bs-target="#' + tabID + '"]');
        next === null || next === void 0 ? void 0 : next.dispatchEvent(new Event('show.bs.tab'));
        setTimeout(() => {
            next === null || next === void 0 ? void 0 : next.dispatchEvent(new Event('shown.bs.tab'));
        });
        // Just figure out the index of the target tab to be able to update our activeTab
        let nextActiveIndex = 0;
        tabs.forEach((tab, i) => {
            if (tab[0] === tabID) {
                nextActiveIndex = i;
            }
        });
        setActiveTab(nextActiveIndex);
    };
    return (react_1.default.createElement("div", { className: className || (0, classnames_1.default)('tabs', ...classNames), ref: ref },
        react_1.default.createElement("ul", { className: "nav nav-tabs", role: "tablist" }, tabs.map(([tabID, title], i) => (react_1.default.createElement("li", { className: "nav-item", role: "presentation", key: tabID },
            react_1.default.createElement("button", { className: (0, classnames_1.default)('nav-link', (activeTab == i) && 'active'), id: `${tabID}-tab`, "data-bs-toggle": "tab", "data-bs-target": `#${tabID}`, type: "button", role: "tab", "aria-controls": tabID, "aria-selected": (activeTab == i) ? 'true' : 'false', onClick: () => {
                    setActiveTabWithEvents(tabID);
                } }, title))))),
        react_1.default.createElement("div", { className: "tab-content" }, children)));
};
exports.default = Tabs;
